import { BIG_COPY, YES_NO, YES_NO_UNSURE, YES_NO_UNKNOWN, type Option } from "./copy";
import type { BigAnswers, BigStepKey, Contact } from "./types";
import { CAR_ZONES } from "../schadenmeldung/illustrations/CarSchematic";

export interface SummaryRow {
  label: string;
  value: string;
}
export interface SummarySection {
  heading: string;
  target: BigStepKey;
  rows: SummaryRow[];
}

function optLabel(options: Option[], value: string | null): string {
  if (!value) return "";
  return options.find((o) => o.value === value)?.label ?? value;
}
function optLabels(options: Option[], values: string[]): string {
  if (!values.length) return "";
  return values.map((v) => options.find((o) => o.value === v)?.label ?? v).join(", ");
}
function zoneLabels(zones: { id: string; label: string }[], ids: string[]): string {
  if (!ids.length) return "";
  const dict = Object.fromEntries(zones.map((z) => [z.id, z.label]));
  return ids.map((id) => dict[id] ?? id).join(", ");
}
// Format a raw digit string (e.g. "500000") with de-DE 1000s grouping.
function eur(s: string): string {
  const digits = (s || "").replace(/\D/g, "");
  if (!digits) return "";
  return new Intl.NumberFormat("de-DE").format(Number(digits));
}
// One-line summary of a contact block (name · address · tel · mobile · email …).
function contactLine(c: Contact): string {
  return [
    `${c.vorname} ${c.name}`.trim(),
    [c.strasse, `${c.plz} ${c.ort}`.trim()].filter(Boolean).join(", "),
    c.tel,
    c.handy,
    c.email,
    c.funktion,
    c.kuerzel,
  ]
    .filter(Boolean)
    .join(" · ");
}
// Summarise the affected building units, e.g. "3. OG: Mitte, Vorne (vermietet) — Bad".
function einheitenSummary(a: BigAnswers): string {
  const W = BIG_COPY.gebWhere;
  return a.gebEinheiten
    .map((e) => {
      const etage = W.etagen.find((x) => x.value === e.etage)?.label ?? e.etage;
      const lage = optLabels(W.lageOptions, e.lage);
      const verm = e.vermietet === "ja" ? "vermietet" : e.vermietet === "nein" ? "nicht vermietet" : "";
      const tail = [lage, verm && `(${verm})`, e.zusatz].filter(Boolean).join(" ");
      return tail ? `${etage}: ${tail}` : etage;
    })
    .join(" · ");
}
function whenLabel(s: string): string {
  if (!s) return "";
  try {
    return new Date(s).toLocaleString("de-DE");
  } catch {
    return s;
  }
}
// keep only rows with a non-empty value
function rows(...rs: (SummaryRow | null)[]): SummaryRow[] {
  return rs.filter((r): r is SummaryRow => !!r && r.value.trim().length > 0);
}
function row(label: string, value: string): SummaryRow {
  return { label, value };
}

function melderSection(a: BigAnswers, target: BigStepKey): SummarySection {
  const c = BIG_COPY.melder;
  return {
    heading: "Schadenmelder & Ansprechpartner",
    target,
    rows: rows(
      row("Name", `${a.melderVorname} ${a.melderName}`.trim()),
      row(c.tel, a.melderTel),
      row(c.handy, a.melderHandy),
      row(c.email, a.melderEmail),
      row("Kürzel", a.melderKuerzel),
      row("Für Unternehmen / Kunde", a.melderFuerUnternehmen),
      row("Vorgangs-/Schadennummer", a.melderVorgangsnummer),
      row(
        "Ansprechpartner",
        a.apWer === "andere"
          ? contactLine(a.apContact)
          : optLabel(c.apOptions, a.apWer),
      ),
    ),
  };
}

function whenSection(a: BigAnswers, target: BigStepKey): SummarySection {
  return {
    heading: "Schadentag",
    target,
    rows: rows(
      row("Schadendatum", whenLabel(a.schadenDatum)),
      row("Ungefährer Zeitpunkt", a.schadenZeitpunkt),
      row("Entdeckt am", a.entdecktAm),
    ),
  };
}

function gebSections(a: BigAnswers): SummarySection[] {
  const W = BIG_COPY.gebWhere;
  const H = BIG_COPY.gebHergang;
  const V = BIG_COPY.gebVerursacher;
  const AB = BIG_COPY.gebAbrechnung;
  return [
    melderSection(a, "geb_melder"),
    whenSection(a, "geb_when"),
    {
      heading: "Schadenort",
      target: "geb_where",
      rows: rows(
        row("Anschrift", [a.gebStrasse, `${a.gebPlz} ${a.gebOrt}`.trim()].filter(Boolean).join(", ")),
        row("Objekt", a.gebObjektNr),
        row("Wohnung / Einheit-Nr.", a.gebWohneinheit),
        row("Betroffene Einheiten", einheitenSummary(a)),
        row("Sonstige Angaben", a.gebSonstigeAngaben),
        row("Mieter", contactLine(a.gebMieterContact)),
        row("Mieter Hausrat", optLabel(YES_NO, a.gebMieterHausrat)),
        row("Mieter-Versicherer", [a.gebMieterVersicherer, a.gebMieterVsnr].filter(Boolean).join(" · ")),
        row("Beschädigte Sachen", optLabels(W.beschaedigtOptions, a.gebBeschaedigt)),
      ),
    },
    {
      heading: "Schadenart",
      target: "geb_art",
      rows: rows(
        row("Gefahr", optLabel(BIG_COPY.gebArt.options, a.gebSchadenart)),
        row(
          "Untergefahr",
          [
            a.gebSchadenart
              ? optLabel(BIG_COPY.gebArt.untergefahren[a.gebSchadenart] ?? [], a.gebUntergefahr)
              : "",
            a.gebUntergefahrDetail,
          ]
            .filter(Boolean)
            .join(" — "),
        ),
      ),
    },
    {
      heading: "Schadenhergang & Höhe",
      target: "geb_hergang",
      rows: rows(
        row("Hergang", a.gebHergang),
        row("Schadenstelle", a.gebSchadenstelle),
        row("Ursache", optLabel(H.ursacheOptions, a.gebUrsache)),
        row("Folgeschäden", a.gebFolgeschaeden),
        row("Betroffene Teile", a.gebBetroffeneTeile),
        row("Geschätzte Höhe", a.gebSchadenhoehe ? `${eur(a.gebSchadenhoehe)} EUR` : ""),
        row("Nutzung möglich", [optLabel(YES_NO, a.gebNutzungMoeglich), a.gebNutzungDetail].filter(Boolean).join(" — ")),
      ),
    },
    {
      heading: "Maßnahmen & Sanierung",
      target: "geb_massnahmen",
      rows: rows(
        row("Maßnahme beauftragt", optLabel(YES_NO, a.gebMassnahmeBeauftragt)),
        row("Was unternommen", a.gebMassnahmeWas),
        row("Beauftragte Firma", a.gebFirma),
        row("Auftragsnummer", a.gebAuftragsnummer),
        row("Kostenvoranschlag", optLabel(YES_NO, a.gebKostenvoranschlag)),
        row("Rechnungen", optLabel(YES_NO, a.gebRechnungen)),
        row("Sanierung/Verbesserung", optLabel(YES_NO_UNSURE, a.gebSanierung)),
      ),
    },
    {
      heading: "Verursacher, Polizei & weitere Geschädigte",
      target: "geb_verursacher",
      rows: rows(
        row("Verursacher bekannt", optLabel(YES_NO_UNSURE, a.gebVerursacher)),
        row("Verursacher", contactLine(a.gebVerursacherContact)),
        row("Haftpflicht", optLabel(YES_NO, a.gebVerursacherHaftpflicht)),
        row("Versicherer", [a.gebVerursacherVersicherer, a.gebVerursacherVsnr].filter(Boolean).join(" · ")),
        row("Polizeilich gemeldet", optLabel(V.polizeiOptions, a.gebPolizei)),
        row("Aktenzeichen / Dienststelle", [a.gebPolizeiAktenzeichen, a.gebPolizeiDienststelle].filter(Boolean).join(" · ")),
        row("Weitere Geschädigte", optLabel(YES_NO, a.gebWeitere)),
        row("Weitere Geschädigte (Daten)", contactLine(a.gebWeitereContact)),
        row("Art des Schadens (weitere)", a.gebWeitereArt),
      ),
    },
    {
      heading: "Steuer, Zahlung & Hinweise",
      target: "geb_abrechnung",
      rows: rows(
        row("Vorsteuerabzug", optLabel(AB.vorsteuerOptions, a.gebVorsteuer) + (a.gebVorsteuer === "ja" && a.gebVorsteuerAnteil ? ` (${a.gebVorsteuerAnteil} %)` : "")),
        row("Zahlungsempfänger", [optLabel(AB.zahlungOptions, a.gebZahlungEmpfaenger), a.gebZahlungName].filter(Boolean).join(": ")),
        row("IBAN", a.gebIban),
        row("BIC", a.gebBic),
        row("Zusätzliche Informationen", a.gebZusatz),
      ),
    },
    {
      heading: "Unterlagen",
      target: "geb_unterlagen",
      rows: rows(row("Vorhanden", optLabels(BIG_COPY.unterlagen.gebOptions, a.gebUnterlagen))),
    },
  ];
}

function kfzSections(a: BigAnswers): SummarySection[] {
  const HG = BIG_COPY.kfzHergang;
  const S = BIG_COPY.kfzSachen;
  const has = (k: string) => a.kfzSchaeden.includes(k);
  const out: SummarySection[] = [
    {
      heading: "Betroffene Schäden",
      target: "kfz_schaeden",
      rows: rows(row("Schäden", optLabels(BIG_COPY.kfzSchaeden.options, a.kfzSchaeden))),
    },
    melderSection(a, "kfz_melder"),
    whenSection(a, "kfz_when"),
    {
      heading: "Schadenort",
      target: "kfz_where",
      rows: rows(row("Ort", a.kfzOrt)),
    },
    {
      heading: "Schadenhergang",
      target: "kfz_art",
      rows: rows(
        row("Art", optLabel(BIG_COPY.kfzArt.options, a.kfzArt)),
        row("Unfalltyp", optLabel(BIG_COPY.kfzArt.unfallOptions, a.kfzUnfallTyp)),
        row("Schadentyp", optLabel(BIG_COPY.kfzArt.ohneFremdOptions, a.kfzOhneFremdTyp)),
      ),
    },
  ];
  if (has("eigenes")) {
    out.push({
      heading: "Eigenes Fahrzeug",
      target: "kfz_eigenes",
      rows: rows(
        row("Kennzeichen", a.kfzEigenKennzeichen),
        row("Fahrzeugart", a.kfzEigenArt),
        row("Kilometerstand", a.kfzEigenKm),
        row("Anhänger", optLabel(YES_NO, a.kfzEigenAnhaenger) + (a.kfzEigenAnhaengerKz ? ` (${a.kfzEigenAnhaengerKz})` : "")),
        row("Fahrer", contactLine(a.kfzEigenFahrer)),
        row("Beschädigte Bereiche", zoneLabels(CAR_ZONES, a.kfzEigenZonen)),
        row("Abgeschleppt/gesichert", optLabel(YES_NO, a.kfzEigenAbgeschleppt)),
        row("Geschätzte Höhe", a.kfzEigenHoehe ? `${eur(a.kfzEigenHoehe)} EUR` : ""),
        row("Werkstatt eingeschaltet", optLabel(YES_NO, a.kfzEigenWerkstatt)),
        row("Gutachter eingeschaltet", optLabel(YES_NO, a.kfzEigenGutachter)),
      ),
    });
  }
  if (has("fremdes")) {
    const multi = a.kfzFremdFahrzeuge.length > 1;
    out.push({
      heading: "Fremdes Fahrzeug / Unfallgegner",
      target: "kfz_fremdes",
      rows: a.kfzFremdFahrzeuge.flatMap((f, i) => {
        const pre = multi ? `Fzg. ${i + 1} – ` : "";
        return rows(
          row(`${pre}Kennzeichen`, f.kennzeichen),
          row(`${pre}Fahrzeugart`, f.art),
          row(`${pre}Hersteller / Modell`, f.hersteller),
          row(`${pre}Farbe`, f.farbe),
          row(`${pre}Anhänger`, optLabel(YES_NO, f.anhaenger)),
          row(`${pre}Fahrbereit`, optLabel(YES_NO, f.fahrbereit)),
          row(`${pre}Fahrer`, contactLine(f.fahrer)),
          row(`${pre}Halter / Eigentümer`, contactLine(f.halter)),
          row(`${pre}Versicherer bekannt`, optLabel(YES_NO_UNKNOWN, f.versichererBekannt)),
          row(`${pre}Versicherer`, [f.versicherer, f.vsnr, f.schadennr, f.ansprech].filter(Boolean).join(" · ")),
          row(`${pre}Vorsteuerabzug`, optLabel(YES_NO_UNKNOWN, f.vorsteuer)),
          row(`${pre}Beschädigte Bereiche`, zoneLabels(CAR_ZONES, f.zonen)),
          row(`${pre}Abgeschleppt`, optLabel(YES_NO, f.abgeschleppt)),
          row(`${pre}Geschätzte Höhe`, f.hoehe ? `${eur(f.hoehe)} EUR` : ""),
          row(`${pre}KV/Rechnung/Gutachten`, optLabel(YES_NO, f.kv)),
          row(`${pre}Mietwagen/Nutzungsausfall`, [optLabel(YES_NO_UNSURE, f.mietwagen), f.mietwagenDetail].filter(Boolean).join(" — ")),
        );
      }),
    });
  }
  out.push({
    heading: "Einschätzung, Zeugen & Skizze",
    target: "kfz_hergang",
    rows: rows(
      row("Einschätzung Hergang", optLabel(HG.einschaetzungOptions, a.kfzEinschaetzung)),
      row("Zeugen", optLabel(YES_NO_UNKNOWN, a.kfzZeugen)),
      row("Zeugen (Daten)", contactLine(a.kfzZeugenContact)),
      row("Zeugenaussage", a.kfzZeugenAussage),
      row("Unfallskizze/-bericht", optLabels(HG.skizzeOptions, a.kfzSkizze)),
      row("Polizeilich gemeldet", optLabel(BIG_COPY.gebVerursacher.polizeiOptions, a.kfzPolizei)),
      row("Aktenzeichen / Dienststelle", [a.kfzPolizeiAktenzeichen, a.kfzPolizeiDienststelle].filter(Boolean).join(" · ")),
    ),
  });
  if (has("person")) {
    const P = BIG_COPY.kfzPerson;
    const multi = a.kfzPersonen.length > 1;
    out.push({
      heading: "Personenschaden",
      target: "kfz_person",
      rows: a.kfzPersonen.flatMap((p, i) => {
        const pre = multi ? `Person ${i + 1} – ` : "";
        return rows(
          row(`${pre}Verletzte Person`, p.name),
          row(`${pre}Rolle`, [optLabel(P.rolleOptions, p.rolle), p.rolleDetail].filter(Boolean).join(" — ")),
          row(`${pre}Gehört zum Unternehmen`, optLabel(P.zugehoerigOptions, p.zugehoerig)),
          row(`${pre}Verletzung`, p.verletzung),
          row(`${pre}Arbeitsunfähig`, optLabel(YES_NO_UNKNOWN, p.arbeitsunfaehig)),
          row(`${pre}Ärztliche Unterlagen`, optLabel(YES_NO, p.unterlagen)),
        );
      }),
    });
  }
  if (has("sonstige")) {
    out.push({
      heading: "Sonstige Sachen oder Gegenstände",
      target: "kfz_sachen",
      rows: rows(
        row("Beschreibung", a.kfzSachenBeschreibung),
        row("Eigentümer", optLabel(S.eigentuemerOptions, a.kfzSachenEigentuemer)),
        row("Eigentümer (Daten)", a.kfzSachenFremdAngaben),
        row("Geschätzte Höhe", a.kfzSachenHoehe ? `${eur(a.kfzSachenHoehe)} EUR` : ""),
        row("Hergang", a.kfzSachenHergang),
        row("Belege", optLabels(S.belegeOptions, a.kfzSachenBelege)),
        row("Vorsteuerabzug", optLabel(YES_NO_UNKNOWN, a.kfzSachenVorsteuer)),
      ),
    });
  }
  out.push({
    heading: "Unterlagen",
    target: "kfz_unterlagen",
    rows: rows(row("Vorhanden", optLabels(BIG_COPY.unterlagen.kfzOptions, a.kfzUnterlagen))),
  });
  return out;
}

function sonSections(a: BigAnswers): SummarySection[] {
  const SA = BIG_COPY.sonSache;
  const ME = BIG_COPY.sonMensch;
  const SO = BIG_COPY.sonSonst;
  const out: SummarySection[] = [
    {
      heading: "Betroffen",
      target: "son_betroffen",
      rows: rows(row("Kategorie", a.sonBetroffen ? BIG_COPY.sonBetroffen.options[a.sonBetroffen as keyof typeof BIG_COPY.sonBetroffen.options]?.label ?? a.sonBetroffen : "")),
    },
    melderSection(a, "son_melder"),
    whenSection(a, "son_when"),
    {
      heading: "Schadenort",
      target: "son_where",
      rows: rows(row("Anschrift", [a.sonStrasse, `${a.sonPlz} ${a.sonOrt}`.trim()].filter(Boolean).join(", "))),
    },
  ];
  if (a.sonBetroffen === "sache") {
    out.push({
      heading: "Beschädigte Sache",
      target: "son_details",
      rows: rows(
        row("Beschreibung", a.sonSacheBeschreibung),
        row("Eigentümer", optLabel(SA.eigentuemerOptions, a.sonSacheEigentuemer)),
        row("Eigentümer (Daten)", contactLine(a.sonSacheFremdContact)),
        row("Standort der Sache", a.sonSacheOrt),
        row("Hergang", a.sonSacheHergang),
        row("Geschätzte Höhe", a.sonSacheHoehe ? `${eur(a.sonSacheHoehe)} EUR` : ""),
        row("Zeugen", optLabel(YES_NO_UNKNOWN, a.sonSacheZeugen)),
        row("Zeugen (Daten)", contactLine(a.sonSacheZeugenContact)),
        row("Belege", optLabels(SA.belegeOptions, a.sonSacheBelege)),
      ),
    });
  } else if (a.sonBetroffen === "mensch") {
    out.push({
      heading: "Betroffene Person",
      target: "son_details",
      rows: rows(
        row("Gehört zum Unternehmen", optLabel(ME.werOptions, a.sonMenschWer)),
        row("Rolle (dritte/fremde Person)", a.sonMenschRolle),
        row("Person (Daten)", contactLine(a.sonMenschContact)),
        row("Hergang", a.sonMenschHergang),
        row("Verletzung", a.sonMenschVerletzung),
        row("Arbeitsunfähig", optLabel(YES_NO_UNKNOWN, a.sonMenschArbeitsunfaehig)),
        ...a.sonMenschWeitere.flatMap((wp, i) =>
          rows(
            row(`Weitere Person ${i + 1}`, contactLine(wp.contact)),
            row(`Weitere Person ${i + 1} – Verletzung`, wp.verletzung),
          ),
        ),
        row("Zeugen", optLabel(YES_NO_UNKNOWN, a.sonMenschZeugen)),
        row("Zeugen (Daten)", contactLine(a.sonMenschZeugenContact)),
      ),
    });
  } else if (a.sonBetroffen === "sonstiges") {
    out.push({
      heading: "Vorfall",
      target: "son_details",
      rows: rows(
        row("Was ist passiert", a.sonSonstHergang),
        row("Wer ist betroffen", optLabel(SO.werOptions, a.sonSonstWer)),
        row("Betroffener (Daten)", contactLine(a.sonSonstBetroffenContact)),
        row("Geschätzte Höhe", a.sonSonstHoehe ? `${eur(a.sonSonstHoehe)} EUR` : ""),
        row("Anspruch erhoben", optLabel(YES_NO_UNKNOWN, a.sonSonstAnspruch)),
        row("Zeugen", optLabel(YES_NO_UNKNOWN, a.sonSonstZeugen)),
        row("Zeugen (Daten)", contactLine(a.sonSonstZeugenContact)),
      ),
    });
  }
  out.push({
    heading: "Unterlagen",
    target: "son_unterlagen",
    rows: rows(row("Vorhanden", optLabels(BIG_COPY.unterlagen.sonOptions, a.sonUnterlagen))),
  });
  return out;
}

export function buildSections(a: BigAnswers): SummarySection[] {
  if (a.damageType === "gebaeude") return gebSections(a);
  if (a.damageType === "kfz") return kfzSections(a);
  if (a.damageType === "sonstiges") return sonSections(a);
  return [];
}

export function damageTypeLabel(t: BigAnswers["damageType"]): string {
  if (t === "gebaeude") return "Gebäude";
  if (t === "kfz") return "KFZ";
  if (t === "sonstiges") return "Sonstiges";
  return "—";
}

// Pack the full report into a human-readable text (existing backend stores it
// in `description`, capped at 5000 chars).
export function buildDescription(a: BigAnswers): string {
  const lines: string[] = [`Schadenart: ${damageTypeLabel(a.damageType)}`, ""];
  for (const sec of buildSections(a)) {
    if (!sec.rows.length) continue;
    lines.push(`## ${sec.heading}`);
    for (const r of sec.rows) lines.push(`- ${r.label}: ${r.value}`);
    lines.push("");
  }
  if (a.kopie) {
    const k = BIG_COPY.abschluss.kopieOptions.find((o) => o.value === a.kopie)?.label;
    if (k) lines.push(`Kopie der Meldung: ${k}`);
  }
  const text = lines.join("\n").trim();
  return text.length > 4800 ? `${text.slice(0, 4790)}\n…` : text;
}
